import { DOMParser } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";
import {
	EditorView,
	type EditorProps as ProsemirrorEditorProps,
} from "prosemirror-view";
import { createEffect, onCleanup, onMount } from "solid-js";
import { pastePluginKey, submitPluginKey } from "./core-plugins.ts";
import { createPlaceholderPlugin } from "./plugin-placeholder.ts";
import { schema as defaultSchema } from "./schema";

type NodeViewConstructor = any;

// TEMP: backwards compat
export {
	createPlaceholderPlugin,
	placeholderPluginKey,
} from "./plugin-placeholder.ts";

export type EditorOptions = {
	schema?: typeof defaultSchema;
	createState: (schema: typeof defaultSchema) => EditorState;
	nodeViews?: (view: EditorView) => Record<string, NodeViewConstructor>;
	handleDOMEvents?: ProsemirrorEditorProps["handleDOMEvents"];
	autofocus?: boolean;
};

export type EditorViewProps = {
	placeholder?: string;
	disabled?: boolean;
	onUpload?: (file: File) => void;
	onSubmit?: (text: string) => boolean | Promise<boolean>;
	onChange?: (state: EditorState) => void;
	channelId?: string;
	submitOnEnter?: boolean;
	autofocus?: boolean;
	/** anchor the focus ring to the `.editor` wrapper instead of `.ProseMirror` */
	focusRingTarget?: boolean;
};

type ViewInstance = {
	view: EditorView;
	placeholderPlugin: ReturnType<typeof createPlaceholderPlugin>;
	props: EditorViewProps;
	pendingUpdate?: boolean;
	lastOnSubmit: EditorViewProps["onSubmit"];
	lastSubmitOnEnter: EditorViewProps["submitOnEnter"];
	lastOnUpload: EditorViewProps["onUpload"];
	lastPlaceholder: EditorViewProps["placeholder"];
};

const viewInstances = new WeakMap<EditorView, ViewInstance>();

function scheduleUpdate(instance: ViewInstance, newProps: EditorViewProps) {
	if (instance.pendingUpdate) return;
	instance.pendingUpdate = true;

	queueMicrotask(() => {
		instance.pendingUpdate = false;
		const { view, placeholderPlugin } = instance;
		if (view.isDestroyed) return;

		const needsUpdate =
			instance.lastOnSubmit !== newProps.onSubmit ||
			instance.lastSubmitOnEnter !== newProps.submitOnEnter ||
			instance.lastOnUpload !== newProps.onUpload ||
			instance.lastPlaceholder !== newProps.placeholder;

		if (needsUpdate) {
			const tr = view.state.tr;
			tr.setMeta(submitPluginKey, {
				onSubmit: newProps.onSubmit,
				submitOnEnter: newProps.submitOnEnter ?? true,
			});
			tr.setMeta(pastePluginKey, {
				onUpload: newProps.onUpload,
			});
			if (newProps.placeholder !== undefined) {
				tr.setMeta(placeholderPlugin, newProps.placeholder ?? "");
			}
			view.dispatch(tr);

			instance.props = newProps;
			instance.lastOnSubmit = newProps.onSubmit;
			instance.lastSubmitOnEnter = newProps.submitOnEnter ?? true;
			instance.lastOnUpload = newProps.onUpload;
			instance.lastPlaceholder = newProps.placeholder;
		}
	});
}

export const createEditor = (opts: EditorOptions) => {
	const schema = opts.schema ?? defaultSchema;
	let editorRef!: HTMLDivElement;
	let view: EditorView | undefined;

	return {
		schema,
		setState(state?: EditorState) {
			view?.updateState(state ?? opts.createState(schema));
		},
		focus() {
			view?.focus();
		},
		get view() {
			return view;
		},
		View(props: EditorViewProps) {
			let placeholderPlugin:
				| ReturnType<typeof createPlaceholderPlugin>
				| undefined;
			let initialized = false;
			let lastDisabled = props.disabled ?? false;

			onMount(() => {
				placeholderPlugin = createPlaceholderPlugin();
				view = new EditorView(editorRef!, {
					domParser: DOMParser.fromSchema(schema),
					state: opts.createState(schema),
					nodeViews: opts.nodeViews?.(view!),
					handleDOMEvents: opts.handleDOMEvents,
					editable: () => !(props.disabled ?? false),
					dispatchTransaction(tr) {
						const newState = view?.state.apply(tr);
						if (!newState || !view) return;
						view.updateState(newState);
						props.onChange?.(newState);
					},
				});

				if (props.autofocus ?? opts.autofocus ?? true) {
					view.focus();
				}

				// Initialize plugin state on mount
				const tr = view.state.tr;
				tr.setMeta(submitPluginKey, {
					onSubmit: props.onSubmit,
					submitOnEnter: props.submitOnEnter ?? true,
				});
				tr.setMeta(pastePluginKey, {
					onUpload: props.onUpload,
				});
				if (props.placeholder) {
					tr.setMeta(placeholderPlugin, props.placeholder);
				}
				view.dispatch(tr);

				viewInstances.set(view, {
					view,
					placeholderPlugin,
					props,
					lastOnSubmit: props.onSubmit,
					lastSubmitOnEnter: props.submitOnEnter ?? true,
					lastOnUpload: props.onUpload,
					lastPlaceholder: props.placeholder,
				});
				initialized = true;
			});

			onCleanup(() => {
				if (view) {
					viewInstances.delete(view);
				}
				view?.destroy();
			});

			createEffect(() => {
				// access properties to track them
				props.disabled;
				props.placeholder;
				props.onSubmit;
				props.submitOnEnter;
				props.onUpload;

				if (view && !view.isDestroyed) {
					const instance = viewInstances.get(view);
					if (instance) {
						if (lastDisabled !== (props.disabled ?? false)) {
							lastDisabled = props.disabled ?? false;
							view.setProps({
								editable: () => !lastDisabled,
							});
						}
						scheduleUpdate(instance, props);
					}
				}
			});

			return (
				<div
					class="editor"
					data-focus-ring-target={props.focusRingTarget ? "" : undefined}
					classList={{ disabled: props.disabled ?? false }}
					ref={editorRef!}
					role="textbox"
					aria-label="chat input"
					aria-placeholder={props.placeholder}
					aria-multiline="true"
				></div>
			);
		},
	};
};
